// Fuente unica de las ETIQUETAS DE PERIODO y de BASE DE COMPARACION, espejo
// exacto de utils/statusLabels.ts para los nombres del semaforo (OPSENG-3090).
// Regla espejo de la de colores y la de nombres: prohibido armar la etiqueta
// dentro de un componente. Un `${monthName(m)} ${y}` en un .vue es un bug.
//
// Por que existe: un score sin periodo no se explica solo, y una flecha sin
// base de comparacion tampoco. Quien llega por un link directo o por un correo
// no ve el selector de periodo de la pagina, asi que el numero tiene que
// declarar de cuando es en el propio componente.
//
// Formato es-CL y ninguna variante: mes en minuscula dentro de la frase, guion
// simple en el rango, el ano una sola vez cuando los dos extremos lo comparten.
// Prohibidos "2026-08", "8/2026" y los meses en ingles.

export interface Period {
  year: number
  month: number
}

export interface PeriodRange {
  from: Period
  to: Period
}

// La tabla vive AQUI y en minuscula, que es la forma que se usa dentro de una
// frase. La capitalizada se deriva con sentenceCase: al reves obligaria a
// destapar la mayuscula en cada sitio, que es como aparecio "Logro · Agosto
// 2026" conviviendo con "vs julio". formatters.ts consume esta tabla.
export const MONTH_LABELS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
] as const

function isValidMonth(month: number): boolean {
  return Number.isInteger(month) && month >= 1 && month <= 12
}

// Un mes fuera de rango no se inventa ni se esconde: se cae al ano solo, que
// sigue siendo cierto. Taparlo con "enero" seria mentir sobre el dato.
export function monthLabel(month: number): string {
  return isValidMonth(month) ? MONTH_LABELS[month - 1] : ''
}

export function periodLabel(period: Period): string {
  const month = monthLabel(period.month)
  return month ? `${month} ${period.year}` : String(period.year)
}

export function periodRangeLabel(range: PeriodRange): string {
  const { from, to } = range
  if (from.year === to.year && from.month === to.month) return periodLabel(from)

  // El ano se dice una sola vez cuando los dos extremos lo comparten; con un
  // mes invalido la forma compacta dejaria un guion abriendo la etiqueta.
  if (from.year === to.year && isValidMonth(from.month) && isValidMonth(to.month)) {
    return `${monthLabel(from.month)}-${monthLabel(to.month)} ${to.year}`
  }

  return `${periodLabel(from)}-${periodLabel(to)}`
}

export function accumulatedLabel(range: PeriodRange): string {
  return `acumulado ${periodRangeLabel(range)}`
}

// El guion vacio de una variacion sin comparable (OPSENG-3073) no explica por
// que esta vacio: esta frase es la que lo explica, en title y aria.
export function comparisonLabel(previous: Period | null | undefined): string {
  return previous ? `vs ${periodLabel(previous)}` : 'sin período anterior'
}

function toRange(scope: Period | PeriodRange): PeriodRange {
  return 'from' in scope ? scope : { from: scope, to: scope }
}

// R4: el score del periodo y el promedio acumulado son dos numeros distintos y
// llevan dos etiquetas distintas. Ninguna se abrevia a "Score" cuando conviven
// en pantalla, que era exactamente la confusion del feedback.
// Solo DOS sobrecargas, y a proposito: una tercera con la union de ambas las
// anulaba a las dos -`scoreCaption('period', rango)` compilaba y devolvia
// "Score de marzo 2026", tirando el `to` a la basura-. En un modulo cuyo
// trabajo es ser la unica forma segura de escribir una etiqueta, el tipo tiene
// que atajar eso.
export function scoreCaption(kind: 'period', scope: Period): string
export function scoreCaption(kind: 'accumulated', scope: PeriodRange): string
export function scoreCaption(
  kind: 'period' | 'accumulated',
  scope: Period | PeriodRange,
): string {
  if (kind === 'accumulated') return `Promedio acumulado ${periodRangeLabel(toRange(scope))}`
  return `Score de ${periodLabel('from' in scope ? scope.from : scope)}`
}

// Capitaliza SOLO al abrir frase o titulo. La etiqueta canonica es la minuscula
// y esta funcion es la unica autorizada a alterarla.
export function sentenceCase(label: string): string {
  if (!label) return label
  return label.charAt(0).toUpperCase() + label.slice(1)
}
